import uuidv4 from 'uuid/v4';

import {
  ENTRANCE_TWEEN_DOCUMENT_KEY,
  EMPHASIS_TWEEN_DOCUMENT_KEY,
  EXIT_TWEEN_DOCUMENT_KEY,
  EXIT_ANIMATION_DOCUMENT_KEY,
  ENTRANCE_ANIMATION_DOCUMENT_KEY,
  EMPHASIS_ANIMATION_DOCUMENT_KEY,
  // Importing here and disabling the unused variable warning so we get better documentation.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  DEFAULT_HAND_CURSOR_ID
} from 'js/config/consts';
import {
  ElementType,
  TweenAnimation,
  ScribeElementModelProps,
  NewElementProps,
  ScribeCursorId,
  AnimationConfiguration,
  BlendMode
} from 'js/types';
// Importing here and disabling the unused variable warning so we get better documentation.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { pauseTime as defaultPauseTime, defaultAnimationTime } from 'js/shared/resources/scribedefaults';

class ScribeElementModel {
  /**
   * Unique identifier for the element.
   */
  id: string;

  /**
   * Type of the element.
   */
  type: ElementType;

  /**
   * Width of the element.
   */
  width: number;

  /**
   * Height of the element.
   */
  height: number;

  /**
   * Position of the element on the X-axis.
   */
  x: number;

  /**
   * Position of the element on the Y-axis.
   */
  y: number;

  /**
   * Scale factor of the element along the X-axis.
   */
  scaleX: number;

  /**
   * Scale factor of the element along the Y-axis.
   */
  scaleY: number;

  /**
   * Rotation angle of the element in degrees around its center.
   */
  angle: number;

  /**
   * Origin point of the element on the X-axis.
   * Note: This is not set by the user and is actually assumed to be "left" in the application code.
   */
  originX: string;

  /**
   * Origin point of the element on the Y-axis.
   * Note: This is not set by the user and is actually assumed to be "top" in the applicaiton code
   */
  originY: string;

  /**
   * Indicates whether the element is flipped horizontally.
   */
  flipX: boolean;

  /**
   * Indicates whether the element is flipped vertically.
   */
  flipY: boolean;

  /**
   * Indicates whether the element is locked in the editor.
   */
  locked: boolean;

  /**
   * Indicates whether the element ratio scaling is unlocked in the editor.
   */
  unlockedRatio: boolean;

  /**
   * Indicates whether the element is hidden in the editor (still visible in the output).
   */
  hidden: boolean;

  /**
   * Indicates whether the element is out of all camera views.
   */
  outOfCamera: boolean;

  /**
   * Opacity of the element, ranging from 0 (completely transparent) to 1 (completely opaque).
   */
  opacity: number;

  /**
   * Indicates whether the element is a premium content element.
   */
  isPremium?: boolean;

  /**
   * Indicates whether the element has been stripped in the renderer.
   * This is used internally to optimize rendering performance.
   */
  strippedInRenderer?: boolean;

  /**
   * Pause duration to next element animation - if this is not set (undefined) the application will use the default pause time.
   * See {@link defaultPauseTime} for the default pause time.
   */
  pauseTime?: number;

  /**
   * Entrance animation duration for this element - if this is not set (undefined) the application will use the default entrance animation time.
   * See {@link defaultAnimationTime} for the default entrance animation time.
   */
  animationTime?: number;

  /**
   * Emphasis animation duration for this element - if this is not set (undefined) the application will assume 0.
   * **Note this is different from the entrance and pause animations which have default durations.**
   */
  emphasisAnimationTime?: number;

  /**
   * Exit animation duration for this element - if this is not set (undefined) the application will assume the default exit animation time.
   * **Note this is different from the entrance and pause animations which have default durations.**
   */
  exitAnimationTime?: number;

  /**
   * Animation loops for any emphasis animation applied to the element
   */
  emphasisAnimationLoops?: number;

  /**
   * Identifier for the hand/pen using in an entrance animation. If this is not set the application will use the default cursor id.
   * See {@link DEFAULT_HAND_CURSOR_ID} for the possible cursor identifiers.
   */
  cursorId?: ScribeCursorId;

  /**
   * Entrance animation tween configuration for this element.
   * Note: if this and {@link entranceAnimation} are both undefined the application will use the default entrance animation configuration (hand draw).
   */
  entranceTween?: TweenAnimation;
  /**
   * Entrance animation configuration for this element.
   * Note: if this and {@link entranceTween} are both undefined the application will use the default entrance animation configuration (hand draw).
   */
  entranceAnimation?: AnimationConfiguration;

  /**
   * Emphasis animation tween configuration for this element.
   * Note: if this and {@link emphasisAnimation} are both undefined there will be no emphasis animation applied to the element.
   */
  emphasisTween?: TweenAnimation;
  /**
   * Emphasis animation configuration for this element.
   * Note: if this and {@link emphasisTween} are both undefined there will be no emphasis animation applied to the element.
   */
  emphasisAnimation?: AnimationConfiguration;

  /**
   * Exit animation tween configuration for this element.
   * Note: if this and {@link exitAnimation} are both undefined there will be no exit animation applied to the element.
   */
  exitTween?: TweenAnimation;
  /**
   * Exit animation configuration for this element.
   * Note: if this and {@link exitTween} are both undefined there will be no exit animation applied to the element.
   */
  exitAnimation?: AnimationConfiguration;

  /**
   * Blend mode applied to the element.
   */
  blendMode: BlendMode;

  constructor(data: ScribeElementModelProps | NewElementProps, type: ElementType) {
    if ('id' in data) {
      this.id = data.id;
      this.type = data.type;
      this.width = data.width;
      this.height = data.height;
      this.x = data.x;
      this.y = data.y;

      this.angle = data.angle ?? 0;
      this.scaleY = data.scaleY ?? 1;
      this.scaleX = data.scaleX ?? 1;

      this.flipX = data.flipX ?? false;
      this.flipY = data.flipY ?? false;
      this.originX = data.originX || 'left';
      this.originY = data.originY || 'top';
      this.unlockedRatio =
        typeof data.unlockedRatio !== 'undefined' ? data.unlockedRatio : ['Image', 'Shape'].includes(type);

      // `pauseTime` is a rename of `customPauseTime` so this needs to remain to maintain compatibility
      // with previously stored scribes
      this.pauseTime = typeof data.pauseTime === 'undefined' ? data.customPauseTime : data.pauseTime;

      this.animationTime = data.animationTime;
      this.emphasisAnimationTime = data.emphasisAnimationTime;
      this.emphasisAnimationLoops = data.emphasisAnimationLoops;
      this.exitAnimationTime = data.exitAnimationTime;
      this.locked = data.locked || false;
      this.hidden = data.hidden || false;
      this.outOfCamera = data.outOfCamera || false;
      this.opacity = data.opacity ?? 1;

      this.cursorId = data.cursorId;
      this[ENTRANCE_TWEEN_DOCUMENT_KEY] = data[ENTRANCE_TWEEN_DOCUMENT_KEY];
      this[EMPHASIS_TWEEN_DOCUMENT_KEY] = data[EMPHASIS_TWEEN_DOCUMENT_KEY];
      this[EXIT_TWEEN_DOCUMENT_KEY] = data[EXIT_TWEEN_DOCUMENT_KEY];

      this[EXIT_ANIMATION_DOCUMENT_KEY] = data[EXIT_ANIMATION_DOCUMENT_KEY];
      this[ENTRANCE_ANIMATION_DOCUMENT_KEY] = data[ENTRANCE_ANIMATION_DOCUMENT_KEY];
      this[EMPHASIS_ANIMATION_DOCUMENT_KEY] = data[EMPHASIS_ANIMATION_DOCUMENT_KEY];

      this.isPremium = data.isPremium;
      this.blendMode = data.blendMode ?? BlendMode.Normal;
    } else {
      this.id = uuidv4();
      this.x = 0;
      this.y = 0;
      this.scaleX = 1;
      this.scaleY = 1;
      this.angle = 0;
      this.originX = 'left';
      this.originY = 'top';
      this.flipX = false;
      this.flipY = false;
      this.locked = false;
      this.unlockedRatio = ['Image', 'Shape'].includes(type);
      this.width = data.width;
      this.height = data.height;
      this.hidden = false;
      this.outOfCamera = true;
      this.opacity = 1;
      this.blendMode = BlendMode.Normal;
    }

    this.type = type;
  }
}

export default ScribeElementModel;
